/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { Lease } from '../types/lease.types';

/**
 * What "needs you today" means for a lease.
 *
 * Every rule here reads only fields the *list* endpoint returns, which is
 * what makes the counts above the board true totals: `useLeases` fetches
 * the whole registry, so a predicate over that set describes every lease
 * the agency has, not the page that happened to load.
 *
 * That constraint is why rent arrears are absent, and their absence is the
 * one thing to fix first. A lease in arrears is the loudest thing this band
 * could say, but `GET /agency/{id}/leases` returns the stored contract and
 * nothing derived — the competências live behind a per-lease
 * `/leases/{id}/expectations` call. Counting them here would mean one
 * request per lease on every board render, so the group waits for the list
 * to carry the arrears (or for an aggregate endpoint) rather than being
 * faked from what is at hand.
 */

/** The lease states a running contract can be in. */
const RUNNING_STATES: readonly string[] = ['active', 'extended'];

/**
 * Days without a write before a lease counts as standing still.
 *
 * Deliberately the same week the attendance board uses. A broker moving
 * between the two areas should not have to learn that "parado" means one
 * thing in Atendimentos and another here.
 */
export const LEASE_STALE_AFTER_DAYS = 7;

export type LeaseTriageId = 'draft' | 'noContract' | 'stalled';

/** The groups, in reading order. */
export const LEASE_TRIAGE_IDS: readonly LeaseTriageId[] = [
  'draft', 'noContract', 'stalled',
];

/** The status token each group is painted in, matching the lease states. */
export const LEASE_TRIAGE_TONES: Record<LeaseTriageId, string> = {
  draft: 'var(--status-info-text)',
  noContract: 'var(--status-warning-text)',
  stalled: 'var(--status-error-text)',
};

/**
 * Whole days since a lease was last written to.
 *
 * `updated_at` is what the wire actually carries — the lease has no event
 * log to read a "last fact" from, the way an attendance does. It is
 * therefore a weaker signal on purpose: it moves on any write, including
 * an edit that changed nothing a broker would call progress.
 *
 * @param lease - The lease to date.
 * @param now - Reference instant, injected so tests do not chase the clock.
 * @returns The day count, or null when nothing dates the lease.
 */
export function daysSinceLeaseTouched(lease: Lease, now: number = Date.now()): number | null {
  const stamp = lease.updated_at || lease.created_at;
  if (!stamp) return null;
  const then = new Date(stamp).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now - then) / 86_400_000));
}

/**
 * Whether a lease belongs to a triage group.
 *
 * @param lease - The lease to test.
 * @param id - The group.
 * @param now - Reference instant for the staleness rule.
 * @returns Whether it qualifies.
 */
export function matchesLeaseTriage(
  lease: Lease,
  id: LeaseTriageId,
  now: number = Date.now(),
): boolean {
  if (id === 'draft') return lease.state === 'draft';
  if (id === 'noContract') return isMissingContract(lease);
  return isStaleLease(lease, now);
}

/**
 * Only a running contract can be missing its papers. A draft has not been
 * signed yet, so it is not late with anything; a closed one is history, and
 * nagging about its dossier now helps nobody.
 */
function isMissingContract(lease: Lease): boolean {
  return RUNNING_STATES.includes(lease.state) && (lease.documents?.length ?? 0) === 0;
}

/**
 * An open lease nobody has touched for longer than the staleness window.
 */
function isStaleLease(lease: Lease, now: number): boolean {
  if (!isLeaseOpen(lease)) return false;
  const days = daysSinceLeaseTouched(lease, now);
  return days != null && days >= LEASE_STALE_AFTER_DAYS;
}

/**
 * Whether the lease still has a cycle to run.
 *
 * Terminal leases are excluded from anything that reads as neglect: a
 * contract that ended in March has sat untouched ever since, and that is
 * the correct state of affairs. The board card needs the same test the
 * band does — without it the card painted a rescinded lease in the alarm
 * colour while the band, correctly, left it out of the count.
 *
 * @param lease - The lease to test.
 * @returns Whether it is a draft, running or suspended.
 */
export function isLeaseOpen(lease: Lease): boolean {
  return lease.state === 'draft'
    || RUNNING_STATES.includes(lease.state)
    || lease.state === 'suspended';
}

/**
 * Counts per group, over every lease the page holds.
 *
 * @param leases - The whole registry, as `useLeases` returns it.
 * @param now - Reference instant for the staleness rule.
 * @returns One count per group id.
 */
export function countLeaseTriage(
  leases: readonly Lease[],
  now: number = Date.now(),
): Record<LeaseTriageId, number> {
  return LEASE_TRIAGE_IDS.reduce((counts, id) => ({
    ...counts,
    [id]: leases.filter((lease) => matchesLeaseTriage(lease, id, now)).length,
  }), {} as Record<LeaseTriageId, number>);
}

/**
 * Narrows the registry to one triage group.
 *
 * @param leases - The leases already filtered and searched.
 * @param id - The selected group, or null to leave them alone.
 * @param now - Reference instant for the staleness rule.
 * @returns The matching leases, or the input when nothing is selected.
 */
export function applyLeaseTriage<T extends Lease>(
  leases: readonly T[],
  id: LeaseTriageId | null,
  now: number = Date.now(),
): readonly T[] {
  if (!id) return leases;
  return leases.filter((lease) => matchesLeaseTriage(lease, id, now));
}
